import type * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * The surface everything sits on.
 *
 * Border first, shadow second: the edge is what separates planes in light
 * mode, and a shadow alone disappears entirely in dark mode. Both are defined
 * for both themes so a card never loses its boundary.
 */
// `ComponentProps<'div'>` rather than `HTMLAttributes<HTMLDivElement>` like its
// siblings below, for one reason: `HTMLAttributes` does not include `ref`, and
// under React 19 a function component takes `ref` as an ordinary prop. The
// session player is a Card that has to be measured (its height sets where the
// speaker rail may pin), and it must be measured in place — wrapping a sticky
// element in a measurable div makes that wrapper its containing block and
// stops it sticking. So this one primitive has to be able to pass a ref
// through. The spread below forwards it with everything else.
export function Card({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'rounded-xl border border-line bg-surface shadow-[var(--shadow-card)]',
        className,
      )}
      {...props}
    />
  )
}

export function CardHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-1.5 px-6 pt-5 pb-4', className)} {...props} />
}

export function CardTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn('text-base leading-tight font-semibold', className)} {...props} />
}

export function CardDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-sm leading-relaxed text-ink-muted', className)} {...props} />
}

export function CardContent({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-6 pb-6', className)} {...props} />
}

export function CardFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex items-center gap-3 border-t border-line px-6 py-4', className)}
      {...props}
    />
  )
}

/** A section label above a group of cards. The one repeated type gesture. */
export function SectionLabel({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('eyebrow', className)} {...props} />
}
